import { z } from "zod";

/* ---------------------------------------------------------------------------
 * The page lifecycle.
 *
 * A page has no status column. Status is a projection of its event stream, so
 * the record and the display can never disagree, and the audit trail is not a
 * second thing we have to remember to write. `page_event` is append-only at the
 * database grant level; see migrations/002_append_only.sql.
 *
 * A page says who is asking, which service or person, how urgent, what kind
 * of consult, where, and how to call back, with optional context in the
 * sender's words. The discussion that follows is a thread on the page itself
 * (bundle 6, migration 009), append-only like the event stream.
 * ------------------------------------------------------------------------ */

export const URGENCIES = ["STAT", "Routine"] as const;
export type Urgency = (typeof URGENCIES)[number];

export const EVENT_TYPES = [
  "SENT",
  "DELIVERED",
  "SEEN",
  "ACK",
  "DECLINE",
  "CANCEL",
  "COMPLETED",
  "REM1",
  "REM2",
  "ALERT",
  "ESCALATE",
  "GAP",
  "GAP_RESOLVED",
  "REPAGE",
  "REDIRECT",
] as const;
export type PageEventType = (typeof EVENT_TYPES)[number];

/** Events that mean the escalation chain had to intervene. */
export const ESCALATION_EVENTS: ReadonlySet<string> = new Set([
  "REM1",
  "REM2",
  "ALERT",
  "ESCALATE",
]);

export interface PageEvent {
  id: string;
  pageId: string;
  type: PageEventType;
  label: string;
  at: string; // ISO 8601
  actor: string | null; // clinician id, or null when the system acted
}

/* ---------------------------------------------------------------------------
 * Picklists
 *
 * Everything a clinician can say about a page, other than who and where, is a
 * row the hospital maintains. Mirrors the unit, consult_type and page_outcome
 * tables.
 * ------------------------------------------------------------------------ */

export interface Unit {
  id: string;
  name: string;
  short: string;
  /** "Wards" or "Areas"; the picker lists each group together. */
  grp: string | null;
}

export interface ConsultType {
  id: string;
  service: string | null; // null: offered to every service
  label: string;
  /** False for follow-ups: the recipient can only mark Contacted. */
  canDecline: boolean;
}

export const OUTCOME_KINDS = ["DECLINE", "CANCEL", "COMPLETED"] as const;
export type OutcomeKind = (typeof OUTCOME_KINDS)[number];

export interface Outcome {
  id: string;
  kind: OutcomeKind;
  label: string;
}

/** A service a page can go to. The rota says who covers it right now. */
export interface Service {
  name: string;
  grp: string; // "Medicine", "Surgery", "Acute care"
  /** "ID" for Infectious Diseases; null means the full name is used. */
  short: string | null;
}

/** A service within a division, paged on its own: ID, OPAT. */
export interface SubService {
  division: string;
  name: string;
}

/** A billing code the hospital lists. */
export interface FeeCode {
  code: string;
  label: string;
}

export interface Picklists {
  feeCodes: FeeCode[];
  units: Unit[];
  consultTypes: ConsultType[];
  outcomes: Outcome[];
  services: Service[];
  subServices: SubService[];
}

/** "ID, OPAT" for a service within a division, else the division's name. */
export const serviceLabel = (division: string, sub: string | null, short?: string | null) =>
  sub ? `${short || division}, ${sub}` : division;

export interface Page {
  id: string;
  fromId: string;
  fromName: string;
  toId: string | null; // null while a coverage gap is open
  toName: string | null;
  service: string;
  subService: string | null;
  /** The division's short name, for "ID, OPAT". */
  serviceShort: string | null;
  urgency: Urgency;
  consultTypeId: string;
  consultType: string; // label
  /** Null when the sender chose "No unit": patient location is optional. */
  unitId: string | null;
  unit: string | null; // short name
  unitName: string | null;
  bed: string | null;
  callback: string;
  /** Each person's primary phone from their profile now, for calling them directly. */
  fromPhone: string | null;
  toPhone: string | null;
  sentAt: string;
  site: string;
  events: PageEvent[];
  /** Whether the recipient may decline or redirect, from the consult type. */
  canDecline: boolean;
  /** The page this one was redirected from, and who it was first sent to. */
  redirectedFrom: string | null;
  redirectedFromName: string | null;
  /** The page this one was redirected to, once it has been. */
  redirectedTo: string | null;
  /**
   * Patient details. Always null on the live deployment, which has no such
   * columns; filled only in patient-data mode (the demo).
   */
  patientIdType: "MRN" | "PHN" | null;
  patientId: string | null;
  patientInitials: string | null;
  question: string | null;
  /** The sender's optional free text about the page. */
  context: string | null;
  /** Messages in the page's discussion, and how many the viewer has not read. */
  messageCount: number;
  /** The newest message, for the list's preview line. */
  lastMessage: { body: string; authorId: string; at: string } | null;
  unread: number;
  /** The fee code the recipient billed it under, once they have. */
  feeCode: string | null;
  /** Set when the page went to a team: every member has a copy, sharing broadcastId. */
  teamId: string | null;
  broadcastId: string | null;
  /** Whether the viewer archived or deleted it from their own lists. */
  hidden: "archived" | "deleted" | null;
}

/** A named group one page reaches all at once. */
export interface Team {
  id: string;
  name: string;
  description: string | null;
  createdBy: string;
  members: { id: string; name: string; title: string; service: string }[];
}


/** One message in a page's discussion. */
export interface PageMessage {
  id: string;
  pageId: string;
  authorId: string;
  authorName: string;
  body: string;
  at: string;
}

export const messageSchema = z.object({
  body: z.string().trim().min(1, "Write a message").max(1000, "Keep it under 1000 characters"),
});

/** "MRN 1234567 · J.M.", or null when the page carries no patient. */
export const patientLabel = (p: Pick<Page, "patientIdType" | "patientId" | "patientInitials">) =>
  p.patientId ? `${p.patientIdType} ${p.patientId}${p.patientInitials ? ` · ${p.patientInitials}` : ""}` : null;

/** "4B · bed 12", or just "4B". */
export const whereLabel = (p: Pick<Page, "unit" | "bed">) =>
  p.unit ? (p.bed ? `${p.unit} · bed ${p.bed}` : p.unit) : p.bed ? `bed ${p.bed}` : "No unit";

/* ---------------------------------------------------------------------------
 * Status projection
 * ------------------------------------------------------------------------ */

export const PAGE_STATES = [
  "sent",
  "delivered",
  "seen",
  "accepted",
  "done",
  "declined",
  "cancelled",
  "redirected",
] as const;
export type PageState = (typeof PAGE_STATES)[number];

export interface PageStatus {
  state: PageState;
  /** 1 live and unseen, 2 seen, 3 accepted, 4 resolved. Drives list order. */
  rank: 1 | 2 | 3 | 4;
  label: string;
  /** Nothing left to do on it. */
  resolved: boolean;
}

const has = (p: Pick<Page, "events">, t: PageEventType) =>
  p.events.some((e) => e.type === t);

export function pageStatus(p: Pick<Page, "events">): PageStatus {
  if (has(p, "CANCEL"))
    return { state: "cancelled", rank: 4, label: "Cancelled", resolved: true };
  if (has(p, "REDIRECT"))
    return { state: "redirected", rank: 4, label: "Redirected", resolved: true };
  if (has(p, "DECLINE"))
    return { state: "declined", rank: 4, label: "Declined", resolved: true };
  if (has(p, "COMPLETED"))
    return { state: "done", rank: 4, label: "Done", resolved: true };
  if (has(p, "ACK"))
    return { state: "accepted", rank: 3, label: "Answered", resolved: false };
  if (has(p, "SEEN"))
    return { state: "seen", rank: 2, label: "Seen", resolved: false };
  if (has(p, "DELIVERED"))
    return { state: "delivered", rank: 1, label: "Delivered", resolved: false };
  return { state: "sent", rank: 1, label: "Sent", resolved: false };
}

export const eventAt = (p: Pick<Page, "events">, t: PageEventType): string | null =>
  p.events.find((e) => e.type === t)?.at ?? null;

/** Seconds from sent to first acknowledgement. The number every SLA is about. */
export function acceptSeconds(p: Pick<Page, "events" | "sentAt">): number | null {
  const ack = eventAt(p, "ACK");
  if (!ack) return null;
  return (Date.parse(ack) - Date.parse(p.sentAt)) / 1000;
}

export function escalated(p: Pick<Page, "events">): boolean {
  return p.events.some((e) => ESCALATION_EVENTS.has(e.type));
}

/** Urgency outranks recency: a STAT page must never sort below a routine one. */
const urgencyRank = (u: Urgency) => (u === "STAT" ? 0 : 1);

export function byPriority(a: Page, b: Page): number {
  const ra = pageStatus(a).rank;
  const rb = pageStatus(b).rank;
  if (ra !== rb) return ra - rb;
  if (ra < 4) {
    const ua = urgencyRank(a.urgency);
    const ub = urgencyRank(b.urgency);
    if (ua !== ub) return ua - ub;
  }
  return Date.parse(b.sentAt) - Date.parse(a.sentAt);
}

/* ---------------------------------------------------------------------------
 * Composing a page
 *
 * The same patterns the database enforces (bed_label and callback_number
 * domains in 001_init.sql), so the form can say why before the server does.
 * ------------------------------------------------------------------------ */

export const BED_RE = /^[A-Za-z0-9]{1,6}(-[A-Za-z0-9]{1,4})?$/;
export const CALLBACK_RE = /^\+?[0-9]{3,15}$/;

const blankToNull = (v: unknown) => (typeof v === "string" && v.trim() === "" ? null : v);

export const createPageSchema = z.object({
  toId: z.string().uuid().nullable(),
  /** Page a whole team: every member gets their own copy. toId must be null. */
  teamId: z.string().uuid().nullable().default(null),
  service: z.string().min(1, "A service is required"),
  subService: z.string().min(1).max(40).nullable().default(null),
  urgency: z.enum(URGENCIES),
  consultTypeId: z.string().min(1, "Choose a consult type"),
  // Patient location is optional; blank means "No unit".
  unitId: z.preprocess(blankToNull, z.string().min(1).nullable().default(null)),
  bed: z.preprocess(
    blankToNull,
    z.string().trim().regex(BED_RE, "A short bed label such as 12 or 4B-12").nullable().default(null)
  ),
  callback: z.string().trim().regex(CALLBACK_RE, "Digits only, such as 44712"),
  /** Send even though an open STAT page to the same recipient exists. */
  force: z.boolean().default(false),
  // Patient-data mode only; ignored and stored as nothing on the live deployment.
  patientIdType: z.enum(["MRN", "PHN"]).nullable().default(null),
  patientId: z.preprocess(blankToNull, z.string().trim().regex(/^[0-9]{6,10}$/, "Digits only").nullable().default(null)),
  patientInitials: z.preprocess(blankToNull, z.string().trim().regex(/^[A-Za-z](\.?[A-Za-z]){0,3}\.?$/, "Initials such as J.M.").nullable().default(null)),
  question: z.preprocess(blankToNull, z.string().trim().max(500, "Keep it under 500 characters").nullable().default(null)),
  context: z.preprocess(blankToNull, z.string().trim().max(500, "Keep the context under 500 characters").nullable().default(null)),
});

/**
 * In patient-data mode, what a page must carry. A general inquiry has no
 * patient; everything else names one. Every page asks its question.
 * Returns field name -> message; empty when complete.
 */
export function patientIssues(
  d: { consultTypeId: string; patientIdType: string | null; patientId: string | null; patientInitials: string | null; question: string | null }
): Record<string, string> {
  const out: Record<string, string> = {};
  if (!d.question) out.question = "A clinical question is required";
  if (d.consultTypeId === "general") return out;
  if (!d.patientIdType) out.patientIdType = "Choose MRN or PHN";
  if (!d.patientId) out.patientId = "A patient number is required";
  else if (d.patientIdType === "MRN" && !/^[0-9]{6,8}$/.test(d.patientId)) out.patientId = "An MRN is 6 to 8 digits";
  else if (d.patientIdType === "PHN" && !/^[0-9]{10}$/.test(d.patientId)) out.patientId = "A PHN is 10 digits";
  if (!d.patientInitials) out.patientInitials = "Patient initials are required";
  return out;
}

export type CreatePageInput = z.input<typeof createPageSchema>;
export type CreatePage = z.output<typeof createPageSchema>;

/** Decline and cancel must say why, from the picklist. */
export const outcomeSchema = z.object({
  outcomeId: z.string().min(1, "Choose a reason"),
});

/** Redirect: to a named clinician, or to a service with nobody on call. */
export const redirectSchema = z.object({
  toId: z.string().uuid().nullable(),
  service: z.string().min(1, "Choose who to redirect to"),
  subService: z.string().min(1).max(40).nullable().default(null),
});

/* ---------------------------------------------------------------------------
 * Profiles
 *
 * What a clinician keeps about themselves. The primary phone is the callback
 * prefilled into each page; the alternative sits beside it in the composer.
 * ------------------------------------------------------------------------ */

const phone = (label: string) =>
  z.preprocess(
    (v) => (typeof v === "string" ? (v.replace(/[\s().-]/g, "") || null) : v),
    z.string().regex(CALLBACK_RE, `${label}: digits only, such as 6045551234 or 44712`).nullable().default(null)
  );

export const profileSchema = z.object({
  email: z.preprocess(blankToNull, z.string().trim().toLowerCase().max(120).email("An email such as name@vch.ca").nullable().default(null)),
  phone: phone("Primary phone"),
  phoneAlt: phone("Alternative phone"),
});
export type Profile = z.output<typeof profileSchema>;

export const teamSchema = z.object({
  name: z.string().trim().min(2, "Give the group a name").max(40, "Keep the name under 40 characters"),
  description: z.preprocess(blankToNull, z.string().trim().max(120).nullable().default(null)),
  memberIds: z.array(z.string().uuid()).min(1, "Add at least one member").max(50, "A group has at most 50 members"),
});

/** Completion may say how it ended. */
export const completeSchema = z.object({
  outcomeId: z.string().min(1).nullable().default(null),
  feeCode: z.string().min(1).max(12).nullable().default(null),
});

/** Set or clear the fee code a page is billed under. */
export const billingSchema = z.object({
  feeCode: z.string().min(1).max(12).nullable(),
});

/**
 * Advanced search over the viewer's own pages. Every field is optional; the
 * text matches people, service, context and the discussion.
 */
export const searchSchema = z.object({
  q: z.string().trim().max(80).optional(),
  direction: z.enum(["in", "out"]).optional(),
  state: z.enum(PAGE_STATES).optional(),
  urgency: z.enum(URGENCIES).optional(),
  service: z.string().max(60).optional(),
  subService: z.string().max(40).optional(),
  consultTypeId: z.string().max(40).optional(),
  from: z.string().date().optional(),
  to: z.string().date().optional(),
  hasMessages: z.enum(["yes"]).optional(),
  /** Only the pages this person archived. */
  archived: z.enum(["yes"]).optional(),
});
export type Search = z.infer<typeof searchSchema>;

/* ---------------------------------------------------------------------------
 * Escalation policy
 *
 * The chain an unanswered page walks. Stored per urgency, read by the server
 * ticker. Delays are seconds; the demo clock compresses them so the whole chain
 * is visible in under a minute.
 * ------------------------------------------------------------------------ */

export const ESC_TARGETS = {
  recipient: "The recipient again",
  backup: "Backup on call",
  sender: "Alert the sender",
  head: "Division head",
} as const;
export type EscTarget = keyof typeof ESC_TARGETS;

export const ESC_CHANNELS = { push: "Push" } as const;
export type EscChannel = keyof typeof ESC_CHANNELS;

export interface EscalationStep {
  type: PageEventType;
  afterSeconds: number;
  target: EscTarget;
  channels: EscChannel[];
  label: string;
}

/** Production values, in seconds. */
export const PROD_POLICY: Record<Urgency, EscalationStep[]> = {
  STAT: [
    { type: "REM1", afterSeconds: 300, target: "recipient", channels: ["push"], label: "Reminder 1" },
    { type: "REM2", afterSeconds: 600, target: "backup", channels: ["push"], label: "Reminder 2 · backup notified" },
    { type: "ALERT", afterSeconds: 900, target: "sender", channels: ["push"], label: "Sender alerted" },
  ],
  Routine: [
    { type: "REM1", afterSeconds: 1800, target: "recipient", channels: ["push"], label: "Reminder 1" },
    { type: "REM2", afterSeconds: 3600, target: "backup", channels: ["push"], label: "Reminder 2 · backup notified" },
    { type: "ALERT", afterSeconds: 5400, target: "sender", channels: ["push"], label: "Sender alerted" },
  ],
};

/** Demo clock: the same chain, compressed so it is watchable. */
export const DEMO_POLICY: Record<Urgency, EscalationStep[]> = {
  STAT: [
    { type: "REM1", afterSeconds: 10, target: "recipient", channels: ["push"], label: "Reminder 1" },
    { type: "REM2", afterSeconds: 20, target: "backup", channels: ["push"], label: "Reminder 2 · backup notified" },
    { type: "ALERT", afterSeconds: 35, target: "sender", channels: ["push"], label: "Sender alerted" },
  ],
  Routine: [
    { type: "REM1", afterSeconds: 25, target: "recipient", channels: ["push"], label: "Reminder 1" },
    { type: "REM2", afterSeconds: 50, target: "backup", channels: ["push"], label: "Reminder 2 · backup notified" },
    { type: "ALERT", afterSeconds: 80, target: "sender", channels: ["push"], label: "Sender alerted" },
  ],
};

/** Response targets the hospital sets for itself. */
export interface Sla {
  statAcceptSeconds: number;
  routineAcceptSeconds: number;
}
export const DEFAULT_SLA: Sla = { statAcceptSeconds: 300, routineAcceptSeconds: 1800 };

export const targetFor = (u: Urgency, sla: Sla = DEFAULT_SLA) =>
  u === "STAT" ? sla.statAcceptSeconds : sla.routineAcceptSeconds;

/* ---------------------------------------------------------------------------
 * Small shared formatters
 * ------------------------------------------------------------------------ */

export function secondsLabel(s: number | null): string {
  if (s == null) return "—";
  if (s < 90) return `${Math.round(s)}s`;
  if (s < 5400) return `${Math.floor(s / 60)}m ${String(Math.round(s % 60)).padStart(2, "0")}s`;
  return `${(s / 3600).toFixed(1)}h`;
}

/**
 * A phone number as people read it: 778-836-2005, 1-778-836-2005,
 * +1-778-836-2005. Extensions and anything else stay as typed.
 */
export function formatPhone(n: string | null | undefined): string {
  if (!n) return "";
  const m = /^(\+?1)?(\d{3})(\d{3})(\d{4})$/.exec(n);
  return m ? `${m[1] ? `${m[1]}-` : ""}${m[2]}-${m[3]}-${m[4]}` : n;
}
